import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { MapPin, Phone } from "lucide-react";
import Booking from "@/components/Booking";
import LoginPage from "@/components/LoginPage";
import AdminPage from "@/components/AdminPage";
import AuthCallback from "@/components/AuthCallback";
import StaffPage from "@/components/StaffPage";
import AvailabilityPage from "@/components/AvailabilityPage";

const LOGO_URL =
    "https://customer-assets.emergentagent.com/job_clinic-booking-86/artifacts/lfwsi76p_Logo_CE_fondoBlanco.png";

const Header = () => (
    <header
        className="border-b border-border bg-card"
        data-testid="site-header"
    >
        <div className="max-w-3xl mx-auto px-5 sm:px-8 py-4 flex items-center gap-3">
            <img
                src={LOGO_URL}
                alt="Logo Ciudad Enfermería"
                className="w-11 h-11 rounded-full object-contain bg-white ring-1 ring-border"
                data-testid="header-logo"
            />
            <div className="leading-tight">
                <p className="font-heading font-medium text-base">Ciudad Enfermería</p>
                <p className="text-xs text-muted-foreground">Consultorio · San Luis Potosí</p>
            </div>
        </div>
    </header>
);

const Footer = () => (
    <footer
        className="border-t border-border bg-card py-6 mt-auto"
        data-testid="site-footer"
    >
        <div className="max-w-3xl mx-auto px-5 sm:px-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-sm text-muted-foreground">
            <a
                href="https://maps.app.goo.gl/fTY5BMVx8t2fq7Ys8"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 hover:text-foreground transition-colors"
                data-testid="footer-maps-link"
            >
                <MapPin className="h-4 w-4 shrink-0" />
                <span>Av. Educación 230 Int. 1, Col. Burócrata, SLP</span>
            </a>
            <a
                href="https://wa.me/5214443067793"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 hover:text-[#25D366] transition-colors"
                data-testid="footer-whatsapp-link"
            >
                <Phone className="h-4 w-4" />
                <span>444 306 7793</span>
            </a>
        </div>
    </footer>
);

const Home = () => (
    <div className="min-h-screen flex flex-col bg-background">
        <Header />
        <main
            className="flex-1 py-8 sm:py-12"
            id="booking"
            data-testid="booking-section"
        >
            <div className="max-w-3xl mx-auto px-5 sm:px-8">
                <div className="mb-8 sm:mb-10">
                    <p className="text-xs uppercase tracking-[0.25em] text-muted-foreground mb-2">
                        Reserva tu cita
                    </p>
                    <h1 className="font-heading text-3xl sm:text-4xl font-medium tracking-tight">
                        Agenda en menos de un minuto
                    </h1>
                </div>
                <Booking />
            </div>
        </main>
        <Footer />
    </div>
);

function AppRouter() {
    const location = useLocation();
    // CRITICAL: Detect OAuth callback synchronously during render (not in useEffect)
    if (location.hash?.includes("session_id=")) {
        return <AuthCallback />;
    }
    return (
        <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/enfermeria" element={<StaffPage />} />
            <Route path="/disponibilidad" element={<AvailabilityPage />} />
        </Routes>
    );
}

function App() {
    return (
        <div className="App">
            <BrowserRouter>
                <AppRouter />
                <Toaster richColors position="top-center" />
            </BrowserRouter>
        </div>
    );
}

export default App;
